/**
 * Admin Hook
 * User management and promotion
 */

import { useState, useCallback } from 'react';
import { apiGet, apiPost, apiDelete, ApiError } from '../services/api';

interface UserProfile {
  email: string;
  givenName: string;
  familyName: string;
  role: 'admin' | 'instructor' | 'student';
  createdAt: string;
  lastSeen?: string;
}

interface PromoteUserResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    role: string;
    givenName: string;
    familyName: string;
  };
}

interface DemoteUserResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    role: string;
  };
}

interface SyncUsersResponse {
  success: boolean;
  data: {
    totalSynced: number;
    added: number;
    updated: number;
    users: Array<{ email: string; role: string }>;
  };
}

interface SearchUserResponse {
  success: boolean;
  data: { email: string; sub: string; givenName: string; familyName: string; role: 'admin' | 'instructor' | 'student' };
}

interface AssignRoleResponse {
  success: boolean;
  data: { email: string; role: string };
}

interface FetchUsersResponse {
  success: boolean;
  data: UserProfile[];
}

interface UseAdminResult {
  users: UserProfile[];
  loading: boolean;
  error: string | null;
  fetchUsers: (role?: 'admin' | 'instructor' | 'student') => Promise<void>;
  promoteToInstructor: (email: string) => Promise<PromoteUserResponse>;
  demoteToStudent: (email: string) => Promise<DemoteUserResponse>;
  deleteUser: (email: string) => Promise<void>;
  syncUsers: () => Promise<SyncUsersResponse>;
  searchUser: (email: string) => Promise<SearchUserResponse>;
  assignRole: (email: string, role: string) => Promise<AssignRoleResponse>;
}

/**
 * Hook for admin operations
 */
export function useAdmin(): UseAdminResult {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all users (with optional role filter)
   */
  const fetchUsers = useCallback(async (role?: 'admin' | 'instructor' | 'student') => {
    try {
      setLoading(true);
      setError(null);
      // Add timestamp to prevent browser caching
      const timestamp = Date.now();
      const path = role
        ? `/admin/users?role=${role}&_t=${timestamp}`
        : `/admin/users?_t=${timestamp}`;
      const response = await apiGet<FetchUsersResponse>(path);
      setUsers(response.data);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to fetch users';
      setError(message);
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Promote a user to instructor
   */
  const promoteToInstructor = useCallback(async (email: string): Promise<PromoteUserResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiPost<PromoteUserResponse>('/admin/promote', { email });
      return response;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to promote user';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Demote an instructor to student
   */
  const demoteToStudent = useCallback(async (email: string): Promise<DemoteUserResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiPost<DemoteUserResponse>('/admin/demote', { email });
      return response;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to demote user';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a user
   */
  const deleteUser = useCallback(async (email: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await apiDelete(`/admin/users/${encodeURIComponent(email)}`);

      // Remove from local state
      setUsers(prev => prev.filter(u => u.email !== email));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete user';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Search for a Cognito user by email (works even if they haven't logged in yet)
   */
  const searchUser = useCallback(async (email: string): Promise<SearchUserResponse> => {
    setLoading(true);
    setError(null);
    try {
      return await apiGet<SearchUserResponse>(`/admin/users/search?email=${encodeURIComponent(email)}`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'User not found';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Assign any role to a user by email (handles all transitions including → admin)
   */
  const assignRole = useCallback(async (email: string, role: string): Promise<AssignRoleResponse> => {
    setLoading(true);
    setError(null);
    try {
      return await apiPost<AssignRoleResponse>('/admin/assign-role', { email, role });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to assign role';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sync all users from Cognito to DynamoDB
   */
  const syncUsers = useCallback(async (): Promise<SyncUsersResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiPost<SyncUsersResponse>('/admin/sync-users');
      return response;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to sync users';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    users,
    loading,
    error,
    fetchUsers,
    promoteToInstructor,
    demoteToStudent,
    deleteUser,
    syncUsers,
    searchUser,
    assignRole,
  };
}
